/**
 * Reading the approval chain (build 4).
 *
 * The Approvals board's feed reads it here. The answer is the person's own:
 * which steps they hold and what awaits them, so the key carries the site and
 * a sign-in under another name never reads the last person's answer. Every
 * write on the board invalidates it, and LiveRefresh's blanket invalidation
 * reaches it too.
 */
import { useQuery } from "@tanstack/react-query";
import { useSession } from "../app/SessionContext";
import { DataSourceError } from "./dataSource";

export const approvalKeys = {
  board: (siteId: string, who = "") => ["approvals", siteId, who] as const,
  all: (siteId: string) => ["approvals", siteId] as const,
};

/** A refusal is an answer, not a fault: asking again cannot change it. */
const retryUnlessRefused = (failures: number, e: unknown): boolean =>
  !(e instanceof DataSourceError && (e.status === 401 || e.status === 403)) && failures < 1;

/** The chain's requests at this site, as the platform reads them to the person signed in. */
export function useApprovalsBoard(opts: { enabled: boolean }) {
  const { source, site, principal } = useSession();
  return useQuery({
    queryKey: approvalKeys.board(site.id, principal.subject ?? principal.name),
    queryFn: () => source.approvalsBoard(site.id),
    enabled: opts.enabled,
    staleTime: 15_000,
    refetchInterval: 60_000,
    retry: retryUnlessRefused,
  });
}
